import { differenceInDays, parse } from "date-fns";
import { useEffect } from "react";
import { toast } from "react-toastify";

import { DATE_FORMATS_FNS } from "components";
import ENV from "config/Env";
import { typedQueryClient, useTypedQuery } from "hooks/useTypedQuery";
import { useLocationLicensesDashboardFilters } from "modules/LocationLicenseOverview/components/LocationLicenseFilters/hooks";
import { getPaginatedInvoices, setPaginatedInvoices, usePaginatedInvoices } from "modules/LocationLicenseOverview/hooks";
import { LocationLicenseOverviewKeys } from "modules/LocationLicenseOverview/queryKeys";
import { ApiInstance } from "utils/Api";

import type { HistoricalInvoice, PaginatedHistoricalInvoices } from "modules/LocationLicenseOverview/queryKeys";

type PdfDownloadLicensePayload = {
  document_id: number;
  dealer_location_id: number;
};

export const downloadLocationLicenseInvoice = async (requestData: PdfDownloadLicensePayload) => {
  const response = await ApiInstance.post("/invoices/download", requestData, ENV.exactBaseURL, { responseType: "blob" });
  return response;
};

export const useInvoicesQuery = (paginationUrl = "", currentInvoicePage = 0) => {
  const paginatedInvoices = usePaginatedInvoices();
  const { location: selectedLocation } = useLocationLicensesDashboardFilters();

  const getSortedResults = (data: HistoricalInvoice[]) =>
    [...data].sort((a, b) =>
      differenceInDays(parse(b.Modified, DATE_FORMATS_FNS.dateMonthYearSlash, new Date()), parse(a.Modified, DATE_FORMATS_FNS.dateMonthYearSlash, new Date()))
    );

  const handleUpdatePaginatedInvoices = (data: PaginatedHistoricalInvoices) => {
    const existingLocation = paginatedInvoices.data.find(element => element.location === selectedLocation?.id);

    if (!existingLocation) {
      if (!selectedLocation?.id) return { data: [] };

      const newLocation = { location: selectedLocation.id, data: { [currentInvoicePage]: data } };
      return { data: [...paginatedInvoices.data, newLocation] };
    }

    const updatedData = { ...paginatedInvoices };
    const existingLocationIndex = updatedData.data.findIndex(element => element.location === selectedLocation?.id);

    if (existingLocationIndex !== -1) updatedData.data[existingLocationIndex] = { ...existingLocation, data: { ...existingLocation.data, [currentInvoicePage]: data } };

    return { data: updatedData.data };
  };

  const query = useTypedQuery({
    queryKey: LocationLicenseOverviewKeys.invoices,
    queryFn: async ({ endpoint, baseUrl }) => {
      if (!selectedLocation?.id || !selectedLocation.is_exact_invoicing_enabled || !selectedLocation.exact_account_id) return { __next: null, results: [] };
      const invoices = getPaginatedInvoices();

      if (invoices.data.length > 0) {
        const existingLocationData = invoices.data.find(element => element.location === selectedLocation.id);
        if (existingLocationData?.data[currentInvoicePage]) return existingLocationData.data[currentInvoicePage];
      }

      const response = await ApiInstance.post(
        endpoint,
        { dealer_location_id: selectedLocation.id, ...(paginationUrl && { pagination_url: paginationUrl }), page_index: currentInvoicePage },
        baseUrl
      );

      setPaginatedInvoices(handleUpdatePaginatedInvoices(response.data));

      return {
        __next: response.data.__next,
        results: getSortedResults(response.data.results)
      };
    },
    enabled: Boolean(selectedLocation?.id && selectedLocation.is_exact_invoicing_enabled && selectedLocation.exact_account_id)
  });

  useEffect(() => {
    if (selectedLocation?.id && selectedLocation.is_exact_invoicing_enabled) query.refetch();
    else if (!selectedLocation?.is_exact_invoicing_enabled) typedQueryClient.removeQueries(LocationLicenseOverviewKeys.invoices);
  }, [selectedLocation?.id]);

  useEffect(() => {
    if (query.error) toast.error(query.error.message);
  }, [query.error]);

  return {
    invoices: query.data,
    isLoadingInvoices: query.isLoading,
    paginatedInvoices,
    isRefetching: query.isRefetching,
    refetch: query.refetch
  };
};
